import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { addAlpha } from '@algoan/deep-dive/theme';
import { Spinner } from './spinner';
import { useClickAwayListener, Flag, useFeatureFlag } from '@algoan/dashboard/utils';
import { DownloadBoxIcon } from '../icons';
import { DropdownButton } from './dropdown-button';
import { ListItem } from './list-item';

export type DownloadOption = {
  display: string;
  onDownload: () => void;
  isLoading: boolean;
  icon: JSX.Element;
};

export const DownloadDropdownSkeleton: React.FC = () => {
  return <S.Skeleton />;
};

export const DownloadDropdown: React.FC<{ options: DownloadOption[]; closeDropdown?: boolean }> = ({
  options,
  closeDropdown,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useClickAwayListener<HTMLDivElement>(() => setIsOpen(false));
  const { active: jwsForCFCALIsActivated } = useFeatureFlag(Flag.JWS_FOR_CFCAL);

  useEffect(() => {
    if (closeDropdown) {
      setIsOpen(false);
    }
  }, [closeDropdown]);

  return (
    <S.Container ref={containerRef} open={isOpen} data-testid="download-dropdown">
      <DropdownButton icon={<S.DownloadBox />} isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}></DropdownButton>
      {isOpen && (
        <S.DropdownListContainer isLarge={jwsForCFCALIsActivated}>
          {options.map(({ display, onDownload, isLoading, icon }: DownloadOption, index) => (
            <ListItem key={`${display}-${index}`} onClick={() => onDownload()}>
              {icon}
              <S.ItemText>{display}</S.ItemText>
              {isLoading && <S.Loader />}
            </ListItem>
          ))}
        </S.DropdownListContainer>
      )}
    </S.Container>
  );
};

const S = {
  Skeleton: styled.div`
    background: ${({ theme }) => theme.secondary.chalk};
    border-radius: 4px;
    height: 31px;
    width: 64px;
  `,
  Container: styled.div<{ open: boolean }>`
    position: relative;
    ${({ open, theme }) => (open ? `filter: drop-shadow(0px 2px 13px ${addAlpha(theme.algoanBlue, 0.15)});` : '')}
    z-index: 1;

    &:hover {
      cursor: pointer;
    }
  `,
  DownloadBox: styled(DownloadBoxIcon)`
    margin-right: 7px;

    path {
      fill: ${({ theme }) => theme.secondary.softBlue1};
      fill-opacity: 1;
    }
  `,
  DropdownListContainer: styled.ul<{ isLarge: boolean }>`
    position: absolute;
    right: 0;
    width: ${({ isLarge }) => (isLarge ? '315px' : '240px')};
    background: #ffffff;
    border-radius: 6px 0px 6px 6px;
    margin: 0;
    white-space: nowrap;
    padding: 8px 0px 0px 0px;

    @media print {
      display: none;
    }
  `,
  ItemText: styled.div`
    font-weight: normal;
    font-size: 12px;
    line-height: 15px;
    margin-left: 9px;
    color: ${({ theme }) => theme.algoanBlue};
  `,
  Loader: styled(Spinner)`
    width: 18px;
    height: 18px;
    margin-left: auto;
    margin-right: 16px;
  `,
};
